/**
 * A markdown text field: a formatting toolbar over a plain textarea, and a
 * Preview tab that renders through the same `Markdown` the idea page uses, so
 * what you see is what is stored and shown. The value is the markdown text.
 */

import { useRef, useState, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Bold, Eye, Heading2, Italic, Link, List, ListOrdered, Pencil, Quote } from 'lucide-react';

import { FieldError, fieldA11y } from './FieldError';
import { Markdown } from './Markdown';

type Edit = (text: string, start: number, end: number) => { text: string; start: number; end: number };

/** Wrap the selection (or a placeholder) in a marker, e.g. **bold**. */
function wrap(marker: string, placeholder: string): Edit {
  return (text, start, end) => {
    const inner = text.slice(start, end) || placeholder;
    const next = text.slice(0, start) + marker + inner + marker + text.slice(end);
    return { text: next, start: start + marker.length, end: start + marker.length + inner.length };
  };
}

/** Prefix every line the selection touches, e.g. "- " for a list. */
function prefixLines(prefix: (index: number) => string): Edit {
  return (text, start, end) => {
    const lineStart = text.lastIndexOf('\n', start - 1) + 1;
    const lineEnd = text.indexOf('\n', end) === -1 ? text.length : text.indexOf('\n', end);
    const lines = text.slice(lineStart, lineEnd).split('\n');
    const block = lines.map((line, i) => prefix(i) + line).join('\n');
    const next = text.slice(0, lineStart) + block + text.slice(lineEnd);
    return { text: next, start: lineStart, end: lineStart + block.length };
  };
}

const link: Edit = (text, start, end) => {
  const label = text.slice(start, end) || 'link text';
  const inserted = `[${label}](https://)`;
  const next = text.slice(0, start) + inserted + text.slice(end);
  const urlStart = start + label.length + 3;
  return { text: next, start: urlStart, end: urlStart + 'https://'.length };
};

const toolsFor = (headingPrefix: string): readonly { label: string; icon: LucideIcon; edit: Edit }[] => [
  { label: 'Bold', icon: Bold, edit: wrap('**', 'bold text') },
  { label: 'Italic', icon: Italic, edit: wrap('_', 'italic text') },
  { label: 'Heading', icon: Heading2, edit: prefixLines(() => headingPrefix) },
  { label: 'Bulleted list', icon: List, edit: prefixLines(() => '- ') },
  { label: 'Numbered list', icon: ListOrdered, edit: prefixLines((i) => `${i + 1}. `) },
  { label: 'Quote', icon: Quote, edit: prefixLines(() => '> ') },
  { label: 'Link', icon: Link, edit: link },
];

type Mode = 'write' | 'preview';

function ModeTabs({ mode, onMode }: { mode: Mode; onMode: (mode: Mode) => void }) {
  return (
    <div className="md-editor-modes" role="tablist" aria-label="Description view">
      <button type="button" role="tab" aria-selected={mode === 'write'} className={`md-mode${mode === 'write' ? ' is-active' : ''}`} onClick={() => onMode('write')}>
        <Pencil aria-hidden="true" /> Write
      </button>
      <button type="button" role="tab" aria-selected={mode === 'preview'} className={`md-mode${mode === 'preview' ? ' is-active' : ''}`} onClick={() => onMode('preview')}>
        <Eye aria-hidden="true" /> Preview
      </button>
    </div>
  );
}

export function MarkdownEditor({
  id,
  value,
  onChange,
  maxLength,
  rows = 8,
  placeholder,
  describedBy,
  emptyPreview = 'Nothing to preview yet.',
  footer,
  required = true,
  headingPrefix = '## ',
  error,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  maxLength: number;
  rows?: number;
  placeholder?: string;
  describedBy?: string;
  emptyPreview?: string;
  footer?: ReactNode;
  required?: boolean;
  /** What the Heading tool puts before a line (a document section's body takes `### `). */
  headingPrefix?: string;
  /** What is wrong with the text, shown under the editor and tied to the textarea. */
  error?: string | null;
}) {
  const [mode, setMode] = useState<Mode>('write');
  const area = useRef<HTMLTextAreaElement>(null);

  function apply(edit: Edit) {
    const el = area.current;
    if (!el) return;
    const result = edit(value, el.selectionStart, el.selectionEnd);
    if (result.text.length > maxLength) return;
    onChange(result.text);
    // Restore focus and select what the edit points at, after React re-renders.
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(result.start, result.end);
    });
  }

  return (
    <>
      <div className="md-editor">
        <div className="md-editor-bar">
          <ModeTabs mode={mode} onMode={setMode} />
          {mode === 'write' && (
            <div className="md-editor-tools" role="toolbar" aria-label="Formatting" aria-controls={id}>
              {toolsFor(headingPrefix).map(({ label, icon: Icon, edit }) => (
                <button key={label} type="button" className="icon-btn" aria-label={label} title={label} onClick={() => apply(edit)}>
                  <Icon aria-hidden="true" />
                </button>
              ))}
            </div>
          )}
        </div>
        {mode === 'write' ? (
          <textarea
            ref={area}
            id={id}
            required={required}
            rows={rows}
            maxLength={maxLength}
            value={value}
            placeholder={placeholder}
            {...fieldA11y(id, error, describedBy)}
            onChange={(e) => onChange(e.target.value)}
          />
        ) : (
          <div className="md-editor-preview" style={{ minHeight: `${rows * 1.6}em` }}>
            {value.trim() ? <Markdown>{value}</Markdown> : <p className="hint">{emptyPreview}</p>}
          </div>
        )}
        {footer}
      </div>
      <FieldError inputId={id} message={error} />
    </>
  );
}
